



























































































































































































































import { Component, Vue } from 'vue-property-decorator'
import dayjs from 'dayjs'
import QuestionBankCategoryTree from '@/modules/common/components/question-bank-category-tree.vue'
import {
  createAutoPaper,
  deleteAutoPaper,
  favoriteAutoPaper,
  getAutoPaperList,
  getKnowledgePoints,
  getQuestionBankCategories,
  unfavoriteAutoPaper,
  updateAutoPaper
} from '@/modules/common/api/auto-paper'
import {
  AutoPaperExamMode,
  AutoPaperItem,
  AutoPaperListParams,
  AutoPaperQuestionUsage,
  AutoPaperRule,
  KnowledgePoint,
  QuestionBankCategory,
  QuestionBankCategoryTreeNode
} from '@/modules/common/models/auto-paper'

interface PaperItem {
  id: string
  name: string
  questionCount: number
  createTime: string
  status: 'running' | 'complete'
  favorite: boolean
  examType: string
}

interface PaperTab {
  key: PaperTabKey
  title: string
  icon: string
  count: number | null
}

type CreatePaperMode = 'exam' | 'practice'

interface CreatePaperForm {
  questionUsage: AutoPaperQuestionUsage
  mode: CreatePaperMode
  categories: Array<number>
  questionCount: number
}

type PaperTabKey = 'all' | 'running' | 'complete' | 'favorite'
type PaperStatus = PaperItem['status']

const practiceStatusMap: Record<string, PaperStatus> = {
  in_progress: 'running',
  completed: 'complete'
}

const examModeMap: Record<CreatePaperMode, AutoPaperExamMode> = {
  exam: 'generic',
  practice: 'practice'
}

@Component({
  components: {
    QuestionBankCategoryTree
  }
})
export default class AutoPaper extends Vue {
  activeTab: PaperTabKey = 'all'
  currentPage: number = 1
  pageSize: number = 10
  jumpPage: number = 1
  paperTotal: number = 0
  paperListLoading: boolean = false
  paperListError: boolean = false
  paperListRequestId: number = 0
  favoritePendingIds: Array<string> = []
  deletePendingIds: Array<string> = []
  categoryListLoading: boolean = false
  categoryListError: boolean = false
  categoryMoreLoading: boolean = false
  categoryListRequestId: number = 0
  knowledgePointListLoading: boolean = false
  knowledgePointListError: boolean = false
  knowledgePointListRequestId: number = 0
  categoryCurrentPage: number = 0
  categoryLastPage: number = 1
  categoryTotal: number = 0
  createPaperLoading: boolean = false
  tabTotals: Record<PaperTabKey, number | null> = {
    all: null,
    running: null,
    complete: null,
    favorite: null
  }
  renameDialogVisible: boolean = false
  renameSaving: boolean = false
  editingPaperId: string | null = null
  renameValue: string = ''
  createForm: CreatePaperForm = {
    questionUsage: 'all',
    mode: 'exam',
    categories: [],
    questionCount: 10
  }
  categories: Array<QuestionBankCategoryTreeNode> = []
  knowledgePoints: Array<KnowledgePoint> = []
  selectedKnowledgePointIds: Array<number> = []
  papers: Array<PaperItem> = []

  get tabItems(): Array<PaperTab> {
    return [
      {key: 'all', title: '全部试卷', icon: '▧', count: this.tabTotals.all},
      {key: 'running', title: '进行中', icon: '◷', count: this.tabTotals.running},
      {key: 'complete', title: '已完成', icon: '▧', count: this.tabTotals.complete},
      {key: 'favorite', title: '我的收藏', icon: '☆', count: this.tabTotals.favorite}
    ]
  }

  get pageCount(): number {
    return Math.max(1, Math.ceil(this.paperTotal / this.pageSize))
  }

  get categoryHasMore(): boolean {
    return this.categoryCurrentPage < this.categoryLastPage || this.categories.length < this.categoryTotal
  }

  mounted() {
    window.scrollTo(0, 0)
    this.loadPapers()
    this.loadTabTotals(['running', 'complete', 'favorite'])
    this.loadCategories()
  }

  selectTab(tab: PaperTabKey) {
    if (this.activeTab === tab) return
    this.activeTab = tab
    this.currentPage = 1
    this.jumpPage = 1
    this.loadPapers()
  }

  getListParams(tab: PaperTabKey, page: number = this.currentPage, size: number = this.pageSize): AutoPaperListParams {
    const params: AutoPaperListParams = { page, size }
    if (tab === 'running') params.practice_status = 'in_progress'
    if (tab === 'complete') params.practice_status = 'completed'
    if (tab === 'favorite') params.favorite = true
    return params
  }

  async loadPapers() {
    const requestId = ++this.paperListRequestId
    const currentTab = this.activeTab
    this.paperListLoading = true
    this.paperListError = false
    try {
      const result = await getAutoPaperList(this.getListParams(currentTab))
      if (requestId !== this.paperListRequestId) return
      if (!result || !Array.isArray(result.data) || !result.page) {
        throw new Error('invalid auto paper list response')
      }
      this.papers = result.data.map(item => this.formatPaperItem(item, currentTab))
      this.paperTotal = result.page.total
      this.tabTotals[currentTab] = result.page.total
    } catch {
      if (requestId !== this.paperListRequestId) return
      this.papers = []
      this.paperTotal = 0
      this.paperListError = true
    } finally {
      if (requestId === this.paperListRequestId) this.paperListLoading = false
    }
  }

  async loadTabTotals(tabs: Array<PaperTabKey>) {
    await Promise.all(tabs.map(tab => this.loadTabTotal(tab)))
  }

  async loadTabTotal(tab: PaperTabKey) {
    try {
      const result = await getAutoPaperList(this.getListParams(tab, 1, 1))
      if (result && result.page) this.tabTotals[tab] = result.page.total
    } catch {
      // 页签数量加载失败不影响当前试卷列表的展示和筛选。
    }
  }

  async loadCategories(reset: boolean = true) {
    if (!reset && (this.categoryMoreLoading || !this.categoryHasMore)) return
    const requestId = ++this.categoryListRequestId
    const page = reset ? 1 : this.categoryCurrentPage + 1
    if (reset) {
      this.categoryListLoading = true
      this.categoryListError = false
    } else {
      this.categoryMoreLoading = true
    }
    try {
      const result = await getQuestionBankCategories({
        page,
        size: 10,
        question_usage: this.createForm.questionUsage
      })
      if (requestId !== this.categoryListRequestId) return
      if (!result || !Array.isArray(result.data) || !result.page) throw new Error('invalid category list response')
      const categories = result.data.map(item => this.createCategoryNode(item))
      this.categories = reset ? categories : this.categories.concat(categories)
      this.categoryCurrentPage = result.page.current_page
      this.categoryLastPage = result.page.last_page
      this.categoryTotal = result.page.total
    } catch {
      if (requestId !== this.categoryListRequestId) return
      if (reset) {
        this.categories = []
        this.categoryCurrentPage = 0
        this.categoryLastPage = 1
        this.categoryTotal = 0
        this.categoryListError = true
      } else {
        this.$message({type: 'error', message: '更多分类加载失败，请稍后重试'})
      }
    } finally {
      if (requestId === this.categoryListRequestId) {
        this.categoryListLoading = false
        this.categoryMoreLoading = false
      }
    }
  }

  createCategoryNode(category: QuestionBankCategory): QuestionBankCategoryTreeNode {
    return {
      ...category,
      children: [],
      expanded: false,
      childrenLoaded: false,
      childrenLoading: false,
      childrenError: false,
      childCurrentPage: 0,
      childLastPage: 1,
      childTotal: 0
    }
  }

  loadMoreCategories() {
    this.loadCategories(false)
  }

  async toggleCategoryExpansion(category: QuestionBankCategoryTreeNode) {
    category.expanded = !category.expanded
    if (category.expanded && !category.childrenLoaded) await this.loadChildCategories(category, true)
  }

  async toggleCategorySelection(payload: {category: QuestionBankCategoryTreeNode, checked: boolean}) {
    if (payload.checked) {
      this.selectCategoryNodes(this.getCategoryBranch(payload.category))
    } else {
      this.deselectCategoryNodes(this.getCategoryBranch(payload.category))
    }
    this.syncAncestorCategorySelection(payload.category)
    const hasKnowledgePoints = await this.loadKnowledgePoints()
    if (hasKnowledgePoints) this.scrollToKnowledgePoints()
  }

  getCategoryBranch(category: QuestionBankCategoryTreeNode): Array<QuestionBankCategoryTreeNode> {
    const branch: Array<QuestionBankCategoryTreeNode> = [category]
    category.children.forEach(child => branch.push(...this.getCategoryBranch(child)))
    return branch
  }

  getCategoryAncestors(category: QuestionBankCategoryTreeNode): Array<QuestionBankCategoryTreeNode> {
    const findAncestors = (
      nodes: Array<QuestionBankCategoryTreeNode>,
      ancestors: Array<QuestionBankCategoryTreeNode>
    ): Array<QuestionBankCategoryTreeNode> | null => {
      for (const node of nodes) {
        if (node.id === category.id) return ancestors
        const result = findAncestors(node.children, ancestors.concat(node))
        if (result) return result
      }
      return null
    }
    return findAncestors(this.categories, []) || []
  }

  areAllCategoryChildrenSelected(category: QuestionBankCategoryTreeNode): boolean {
    const allChildrenLoaded = category.childrenLoaded &&
      category.childCurrentPage >= category.childLastPage &&
      category.children.length >= category.childTotal
    return allChildrenLoaded && category.children.length > 0 && category.children.every(child => {
      return this.createForm.categories.indexOf(child.id) !== -1
    })
  }

  selectCategoryNodes(nodes: Array<QuestionBankCategoryTreeNode>) {
    if (!nodes.length) return
    const selectedIds = new Set(this.createForm.categories)
    nodes.forEach(node => selectedIds.add(node.id))
    this.createForm.categories = Array.from(selectedIds)
  }

  deselectCategoryNodes(nodes: Array<QuestionBankCategoryTreeNode>) {
    const deselectedIds = new Set(nodes.map(node => node.id))
    this.createForm.categories = this.createForm.categories.filter(id => !deselectedIds.has(id))
  }

  syncAncestorCategorySelection(category: QuestionBankCategoryTreeNode) {
    const ancestors = this.getCategoryAncestors(category).reverse()
    ancestors.forEach(ancestor => {
      if (this.areAllCategoryChildrenSelected(ancestor)) {
        this.selectCategoryNodes([ancestor])
      } else {
        this.deselectCategoryNodes([ancestor])
      }
    })
  }

  async loadKnowledgePoints(): Promise<boolean> {
    const requestId = ++this.knowledgePointListRequestId
    const categoryIds = this.createForm.categories.slice()
    if (!categoryIds.length) {
      this.knowledgePoints = []
      this.selectedKnowledgePointIds = []
      this.knowledgePointListLoading = false
      this.knowledgePointListError = false
      return false
    }

    this.knowledgePointListLoading = true
    this.knowledgePointListError = false
    try {
      const result = await getKnowledgePoints({
        page: 1,
        size: 100,
        type_ids: categoryIds.join(','),
        question_usage: this.createForm.questionUsage
      })
      if (requestId !== this.knowledgePointListRequestId) return false
      if (!result || !Array.isArray(result.data)) throw new Error('invalid knowledge point response')
      const pointsById = new Map<number, KnowledgePoint>()
      result.data.forEach(point => pointsById.set(point.id, point))
      this.knowledgePoints = Array.from(pointsById.values())
      const visiblePointIds = new Set(this.knowledgePoints.map(point => point.id))
      this.selectedKnowledgePointIds = this.selectedKnowledgePointIds.filter(id => visiblePointIds.has(id))
      return this.knowledgePoints.length > 0
    } catch {
      if (requestId !== this.knowledgePointListRequestId) return false
      this.knowledgePoints = []
      this.knowledgePointListError = true
      return false
    } finally {
      if (requestId === this.knowledgePointListRequestId) this.knowledgePointListLoading = false
    }
  }

  scrollToKnowledgePoints() {
    this.$nextTick(() => {
      const container = this.$refs.categoryKnowledgeScroll as HTMLElement
      const section = this.$refs.knowledgePointSection as HTMLElement
      if (!container || !section) return
      container.scrollTo({
        top: section.offsetTop,
        behavior: 'smooth'
      })
    })
  }

  isKnowledgePointSelected(pointId: number): boolean {
    return this.selectedKnowledgePointIds.indexOf(pointId) !== -1
  }

  toggleKnowledgePointSelection(point: KnowledgePoint) {
    if (!this.isKnowledgePointSelected(point.id)) {
      this.selectedKnowledgePointIds = this.selectedKnowledgePointIds.concat(point.id)
      return
    }
    this.selectedKnowledgePointIds = this.selectedKnowledgePointIds.filter(id => id !== point.id)
  }

  async loadChildCategories(category: QuestionBankCategoryTreeNode, reset: boolean) {
    if (category.childrenLoading) return
    const page = reset ? 1 : category.childCurrentPage + 1
    category.childrenLoading = true
    category.childrenError = false
    try {
      const result = await getQuestionBankCategories({
        fid: category.id,
        page,
        size: 10,
        question_usage: this.createForm.questionUsage
      })
      if (!result || !Array.isArray(result.data) || !result.page) throw new Error('invalid child category response')
      const children = result.data.map(item => this.createCategoryNode(item))
      category.children = reset ? children : category.children.concat(children)
      category.childrenLoaded = true
      category.childCurrentPage = result.page.current_page
      category.childLastPage = result.page.last_page
      category.childTotal = result.page.total
      if (this.createForm.categories.indexOf(category.id) !== -1) {
        this.selectCategoryNodes(children.flatMap(child => {
          return this.getCategoryBranch(child)
        }))
        const hasKnowledgePoints = await this.loadKnowledgePoints()
        if (hasKnowledgePoints) this.scrollToKnowledgePoints()
      }
    } catch {
      category.childrenError = true
    } finally {
      category.childrenLoading = false
    }
  }

  loadMoreChildCategories(category: QuestionBankCategoryTreeNode) {
    this.loadChildCategories(category, false)
  }

  retryChildCategories(category: QuestionBankCategoryTreeNode) {
    this.loadChildCategories(category, true)
  }

  changeQuestionUsage() {
    this.createForm.categories = []
    this.resetKnowledgePoints()
    this.loadCategories(true)
  }

  resetKnowledgePoints() {
    this.knowledgePointListRequestId++
    this.knowledgePointListLoading = false
    this.knowledgePointListError = false
    this.knowledgePoints = []
    this.selectedKnowledgePointIds = []
  }

  formatPaperItem(item: AutoPaperItem, currentTab: PaperTabKey): PaperItem {
    const filteredStatus = currentTab === 'complete'
      ? 'complete'
      : currentTab === 'running' ? 'running' : undefined
    return {
      id: item.id,
      name: item.name || '试卷',
      questionCount: item.question_count,
      createTime: this.formatCreateTime(item.create_time),
      status: practiceStatusMap[item.practice_status || ''] || filteredStatus || 'running',
      favorite: Boolean(item.is_favorited),
      examType: item.exam_mode === 'generic' ? 'exam' : item.exam_mode
    }
  }

  formatPaperName(name: string): string {
    const characters = Array.from(name)
    return characters.length > 15 ? `${characters.slice(0, 15).join('')}…` : name
  }

  formatCreateTime(createTime: number): string {
    const timestamp = Number(createTime)
    if (!Number.isFinite(timestamp) || timestamp <= 0) return '--'
    const milliseconds = timestamp < 1000000000000 ? timestamp * 1000 : timestamp
    return dayjs(milliseconds).format('YYYY-MM-DD HH:mm:ss')
  }

  changePage(page: number) {
    if (page < 1 || page > this.pageCount || page === this.currentPage) return
    this.currentPage = page
    this.jumpPage = page
    this.loadPapers()
  }

  openRenameDialog(paper: PaperItem) {
    this.editingPaperId = paper.id
    this.renameValue = paper.name
    this.renameDialogVisible = true
    this.$nextTick(() => {
      const input: any = this.$refs.renameInput
      if (input && input.focus) input.focus()
    })
  }

  async savePaperName() {
    const name = this.renameValue.trim()
    if (!name || this.renameSaving || !this.editingPaperId) {
      if (this.renameSaving || !this.editingPaperId) return
      this.$message({type: 'warning', message: '请输入试卷名称'})
      return
    }
    this.renameSaving = true
    try {
      const result = await updateAutoPaper(this.editingPaperId, {name})
      if (!result || !result.id) throw new Error('update paper request failed')
      const paper = this.papers.find(item => item.id === this.editingPaperId)
      if (paper) paper.name = result.name || name
      this.renameDialogVisible = false
      this.$message({type: 'success', message: '试卷名称已修改'})
    } catch {
      this.$message({type: 'error', message: '试卷名称修改失败，请稍后重试'})
    } finally {
      this.renameSaving = false
    }
  }

  resetRenameForm() {
    this.editingPaperId = null
    this.renameValue = ''
  }

  async toggleFavorite(paper: PaperItem) {
    if (this.favoritePendingIds.indexOf(paper.id) !== -1) return
    const wasFavorite = paper.favorite
    this.favoritePendingIds.push(paper.id)
    try {
      const result = wasFavorite
        ? await unfavoriteAutoPaper(paper.id)
        : await favoriteAutoPaper(paper.id)
      if (Number(result && result.success) !== 1) throw new Error('favorite request failed')
      paper.favorite = !wasFavorite
      this.$message({
        type: 'success',
        message: wasFavorite ? '取消收藏成功' : '收藏成功'
      })
      if (this.activeTab === 'favorite' && wasFavorite) {
        await this.loadPapers()
      } else {
        await this.loadTabTotal('favorite')
      }
    } catch {
      this.$message({
        type: 'error',
        message: wasFavorite ? '取消收藏失败，请稍后重试' : '收藏失败，请稍后重试'
      })
    } finally {
      this.favoritePendingIds = this.favoritePendingIds.filter(id => id !== paper.id)
    }
  }

  openPaper(paper: PaperItem) {
    if (paper.status === 'complete') {
      this.$router.push({
        name: 'examResult',
        params: {id: paper.id},
        query: {
          examType: paper.examType,
          source: 'autoPaper'
        }
      })
      return
    }
    this.goToExamPage(paper.id, paper.examType)
  }

  goToExamPage(id: string, examType: string) {
    this.$router.push({
      name: 'exam',
      params: {id},
      query: {
        renderType: 'exam',
        examType,
        source: 'autoPaper'
      }
    })
  }

  async deletePaper(paper: PaperItem) {
    if (this.deletePendingIds.indexOf(paper.id) !== -1) return
    try {
      await this.$confirm(`确定删除「${paper.name}」吗？`, '删除试卷', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
    } catch {
      return
    }

    this.deletePendingIds.push(paper.id)
    try {
      const result = await deleteAutoPaper(paper.id)
      if (Number(result && result.success) !== 1) throw new Error('delete request failed')
      if (this.papers.length === 1 && this.currentPage > 1) this.currentPage--
      this.$message({type: 'success', message: '删除成功'})
      await this.loadPapers()
      await this.loadTabTotals(['all', 'running', 'complete', 'favorite'])
    } catch {
      this.$message({type: 'error', message: '删除失败，请稍后重试'})
    } finally {
      this.deletePendingIds = this.deletePendingIds.filter(id => id !== paper.id)
    }
  }

  jumpToPage() {
    const target = Number(this.jumpPage)
    if (!target) return
    const page = Math.min(this.pageCount, Math.max(1, target))
    if (page === this.currentPage) {
      this.jumpPage = page
      return
    }
    this.changePage(page)
  }

  changeQuestionCount(step: number) {
    const current = Number(this.createForm.questionCount) || 1
    this.createForm.questionCount = Math.min(100, Math.max(1, current + step))
  }

  normalizeQuestionCount() {
    const count = Math.round(Number(this.createForm.questionCount))
    this.createForm.questionCount = Number.isFinite(count)
      ? Math.min(100, Math.max(1, count))
      : 1
  }

  buildAutoPaperRules(): Array<AutoPaperRule> {
    const rules: Array<AutoPaperRule> = []
    this.createForm.categories.forEach(categoryId => {
      if (this.selectedKnowledgePointIds.length) {
        this.selectedKnowledgePointIds.forEach(knowledgePointId => rules.push({
          category_id: categoryId,
          knowledge_point_id: knowledgePointId
        }))
        return
      }
      rules.push({category_id: categoryId})
    })
    return rules
  }

  async createPaper() {
    if (this.createPaperLoading) return
    this.normalizeQuestionCount()
    if (!this.createForm.categories.length) {
      this.$message({type: 'warning', message: '请至少选择一个分类'})
      return
    }
    const rules = this.buildAutoPaperRules()
    const examType = this.createForm.mode
    this.createPaperLoading = true
    try {
      const result = await createAutoPaper({
        exam_mode: examModeMap[this.createForm.mode],
        question_count: this.createForm.questionCount,
        question_usage: this.createForm.questionUsage,
        rules
      })
      if (!result || !result.id) throw new Error('create paper request failed')
      this.activeTab = 'all'
      this.currentPage = 1
      this.jumpPage = 1
      this.createForm.categories = []
      this.resetKnowledgePoints()
      this.$message({type: 'success', message: '试卷创建成功'})
      this.goToExamPage(result.id, examType)
    } catch {
      this.$message({type: 'error', message: '试卷创建失败，请稍后重试'})
    } finally {
      this.createPaperLoading = false
    }
  }
}
